import type { HubResponseProps } from '@readme/iso';

import loadable from '@loadable/component';
import React, { useContext } from 'react';

import { UserContext, type UserContextValue } from '@core/context';
import useIsDryRunMode from '@core/hooks/useIsDryRunMode';
import useIsMigrationPreview from '@core/hooks/useIsMigrationPreview';
import { InitializeSuperHubStore, useSuperHubStore } from '@core/store';

import { NotificationToaster } from '@ui/Notification';

import ExposeLocationProxy from './components/ExposeLocationProxy';

/**
 * Code-splitting the EditContainer, ViewContainer and FramePreviewContainer components to avoid
 * loading unnecessary JS when loading the non-admin version of the SuperHub.
 */
const EditContainer = loadable(() => import('./EditContainer'), { ssr: true });
const ViewContainer = loadable(() => import('./ViewContainer'), { ssr: true });
const FramePreviewContainer = loadable(() => import('./FramePreviewContainer'), { ssr: true });

export interface SuperHubProps extends HubResponseProps {
  children?: React.ReactNode;
}

function Content(props: SuperHubProps) {
  const [isSuperHub, isSuperHubAdminOrEditor] = useSuperHubStore(s => [
    s.isSuperHub,
    s.user.role === 'admin' || s.user.role === 'editor',
  ]);

  // Is this a project that is currently being migrated with the migration preview enabled?
  // When the app runs in a migration preview, we only render the view container even for project admins.
  const { isMigrationPreview } = useIsMigrationPreview();
  const { isDryRunMode } = useIsDryRunMode();
  const user = useContext(UserContext) as unknown as UserContextValue;
  const { isGod } = user;

  // Only allow edit mode if not in migration preview mode or dry run mode, unless user is god
  const isEditableInMigrationPreview = (!isMigrationPreview && !isDryRunMode) || isGod;

  // Renders app with the editor and settings UI
  if (isSuperHubAdminOrEditor && isEditableInMigrationPreview && !props.isFramePreview) {
    return <EditContainer {...props} />;
  }

  // Render app with the superhub iframe preview content
  // Note: This is loaded from an iframe mounted within the EditContainer
  if (isSuperHubAdminOrEditor && props.isFramePreview) {
    return <FramePreviewContainer {...props}>{props.children}</FramePreviewContainer>;
  }

  if (isSuperHub) {
    return <ViewContainer {...props} />;
  }

  // For non-superhub projects, render content as is.
  return <>{props.children}</>;
}

/**
 * Top-level wrapper provides our required context that all sub-components need.
 * This allows our top-level SuperHub component to also access context values.
 */
export default function SuperHub({ children, ...rest }: SuperHubProps) {
  return (
    <InitializeSuperHubStore currentBranch={rest.branch} currentVersion={rest.gitVersion} language={rest.i18n.language}>
      <ExposeLocationProxy />
      <Content {...rest}>
        {children}
        <NotificationToaster />
      </Content>
    </InitializeSuperHubStore>
  );
}
